/**
 * Account creation — name/email/phone/password. Wired: POST /api/auth/signup
 * (api.signup). Receives societyId/flatConfigId/flatCode/residentType from
 * /onboarding/verify. On success, routes to /onboarding/pending.
 * Desktop (≥1024): steps rail + centered content, matching the onboarding
 * desktop shell used by society/flat/verify.
 */
import { router, useLocalSearchParams } from 'expo-router';
import React from 'react';
import { View, useWindowDimensions } from 'react-native';

import { space } from '@bero/tokens';
import { InfoNote } from '@/components/gate/bits';
import { Icon } from '@/features/account-admin/icons';
import { BottomBar } from '@/features/community/bits';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Field, Input, Screen, TopBar } from '@/ui';

import { DesktopShell, ONBOARDING_DESKTOP_BREAKPOINT, StepsRail } from './_shell';

type Errors = { fullName?: string; email?: string; phone?: string; password?: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function OnboardingSignupScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { width } = useWindowDimensions();
  const { societyId, flatConfigId, flatCode, residentType, societyName } = useLocalSearchParams<{
    societyId?: string; flatConfigId?: string; flatCode?: string; residentType?: string; societyName?: string;
  }>();

  const [fullName, setFullName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [errors, setErrors] = React.useState<Errors>({});
  const [notice, setNotice] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  if (!societyId) {
    return (
      <Screen scroll={false}>
        <TopBar title="" onBack={() => router.back()} bordered={false} />
        <EmptyState
          icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
          title={t('onb_start_over_title')}
          body={t('onb_signup_start_over_body')}
          cta={t('onb_choose_society')}
          onCta={() => router.replace('/onboarding/society')}
        />
      </Screen>
    );
  }

  const submit = async () => {
    const e: Errors = {};
    if (!fullName.trim()) e.fullName = t('onb_signup_err_name');
    if (!EMAIL_RE.test(email.trim())) e.email = t('onb_signup_err_email');
    if (phone.trim() && phone.trim().replace(/\D/g, '').length < 7) e.phone = t('onb_signup_err_phone');
    if (password.length < 8) e.password = t('onb_signup_min8');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    setNotice(null);
    try {
      await api.signup({
        email: email.trim().toLowerCase(),
        password,
        fullName: fullName.trim(),
        societyId: Number(societyId),
        phone: phone.trim() ? `+91${phone.trim().replace(/\D/g, '')}` : undefined,
        residentType: residentType ?? 'OWNER',
        flatConfigId: flatConfigId ? Number(flatConfigId) : undefined,
      });
      const qs = new URLSearchParams({ residentType: residentType ?? 'OWNER', submittedAt: String(Date.now()) });
      if (flatCode) qs.set('flatCode', flatCode);
      if (societyName) qs.set('societyName', societyName);
      router.replace(`/onboarding/pending?${qs.toString()}`);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 409) {
        setErrors({ email: t('onb_signup_err_email_taken') });
      } else if (status === 404) {
        setNotice(t('onb_signup_err_society_404'));
      } else {
        setNotice((err as { error?: string }).error ?? t('onb_signup_err_generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const body = (
    <>
      {flatCode ? <AppText variant="bodySm" tone="tertiary">{t('onb_signup_flat', { code: flatCode })}</AppText> : null}
      <Field label={t('onb_signup_name_label')} error={errors.fullName}>
        <Input value={fullName} onChangeText={setFullName} placeholder={t('onb_signup_name_ph')} hasError={!!errors.fullName} />
      </Field>
      <Field label={t('onb_signup_email_label')} error={errors.email}>
        <Input
          value={email}
          onChangeText={setEmail}
          placeholder="you@example.com"
          autoCapitalize="none"
          keyboardType="email-address"
          hasError={!!errors.email}
        />
      </Field>
      <Field label={t('onb_signup_phone_label')} error={errors.phone} help={t('onb_signup_phone_help')}>
        <View style={{ flexDirection: 'row', gap: space.s2 }}>
          <View style={{
            width: 56, minHeight: 44, borderRadius: 8, borderWidth: 1, borderColor: colors.border,
            alignItems: 'center', justifyContent: 'center', backgroundColor: colors.canvas0,
          }}>
            <AppText variant="bodyMd" tone="secondary">+91</AppText>
          </View>
          <Input
            value={phone}
            onChangeText={setPhone}
            placeholder="98XXXXXX21"
            keyboardType="phone-pad"
            style={{ flex: 1 }}
            hasError={!!errors.phone}
          />
        </View>
      </Field>
      <Field label={t('onb_signup_password_label')} error={errors.password} help={t('onb_signup_min8')}>
        <Input value={password} onChangeText={setPassword} revealable placeholder="••••••••" hasError={!!errors.password} />
      </Field>
      {notice ? <InfoNote icon="info" tint={colors.danger500}>{notice}</InfoNote> : null}
    </>
  );

  if (width >= ONBOARDING_DESKTOP_BREAKPOINT) {
    return (
      <DesktopShell
        rail={<StepsRail steps={[
          { label: t('onb_step_society'), sub: societyName ?? t('onb_step_society_chosen'), state: 'done' },
          { label: t('onb_step_flat'), sub: flatCode ?? t('onb_step_flat_chosen'), state: 'done' },
          { label: t('onb_step_verify'), sub: t('onb_step_verify_sub'), state: 'done' },
          { label: t('onb_step_account'), sub: t('onb_step_account_sub'), state: 'active' },
        ]} />}
      >
        <View style={{ maxWidth: 480, width: '100%', flex: 1 }}>
          <AppText variant="displayLg">{t('onb_signup_title')}</AppText>
          <AppText variant="bodyMd" tone="secondary" style={{ marginTop: space.s2 }}>
            {t('onb_signup_sub')}
          </AppText>
          <View style={{ marginTop: space.s6, gap: space.s4 }}>{body}</View>
          <View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: space.s3, marginTop: 'auto', paddingTop: space.s8 }}>
            <Button title={t('onb_back')} kind="secondary" onPress={() => router.back()} />
            <Button title={t('onb_signup_submit')} loading={submitting} onPress={() => { void submit(); }} />
          </View>
        </View>
      </DesktopShell>
    );
  }

  return (
    <Screen scroll={false}>
      <TopBar title={t('onb_signup_title')} onBack={() => router.back()} />
      <Screen padded style={{ paddingTop: space.s4, gap: space.s4 }}>
        {body}
      </Screen>
      <BottomBar>
        <Button title={t('onb_signup_submit')} size="lg" loading={submitting} onPress={() => { void submit(); }} />
      </BottomBar>
    </Screen>
  );
}
